import { useEffect, useRef, type RefObject } from 'react';

/**
 * Keeps `<video>`/`<audio>` playback position stable across a signed-URL
 * refresh (backlog/media-url-refresh-playback-position.md).
 *
 * `useMediaUrl` refreshes the signed `src` roughly every ~9 minutes
 * (`expires_in - 60` seconds, mirroring the backend's `MEDIA_URL_TTL_SECONDS`
 * default). Assigning a new `src` to a media element forces the browser to
 * reload the resource and reset `currentTime` to 0 — worst on iOS Safari,
 * present on every browser. For a long-open evaluation/reference page this
 * silently kicks the coach back to the start of the clip mid-scrub.
 *
 * This hook does NOT own the `src` attribute — callers still bind
 * `<video ref={videoRef} src={url}>` directly, exactly as before. It only
 * watches `url` for a REFRESH (old value → new, different value, both
 * truthy) on an element that was already playing, captures `currentTime`/
 * `paused` just before the swap, and restores them once the new source's
 * `loadedmetadata` fires.
 *
 * Deliberately a no-op for:
 *   - the initial mount (`prevUrl` is the same object as `url` on the first
 *     render, so nothing fires)
 *   - the empty-string → real-URL transition some players use for a
 *     first-render race (`prevUrl` is falsy)
 *   - any render where `url` did not actually change (`prevUrl === url`)
 *   - teardown (`url` becomes `null`/`undefined`)
 * so it never fights with a player's own first-load or trim-window logic —
 * see `EnhancedEvaluationDisplay.tsx`'s existing `.load()` effect for the
 * empty→real transition and its template trim-window clamp effect, both of
 * which this hook is designed to coexist with rather than replace.
 *
 * Usage: `useStableMediaSrc(videoRef, streamUrl)` — pass the SAME ref object
 * already bound to the `<video>`/`<audio>` element. One hook call per player.
 */
export function useStableMediaSrc<T extends HTMLMediaElement>(
  mediaRef: RefObject<T | null>,
  url: string | null | undefined
): void {
  const prevUrlRef = useRef<string | null | undefined>(url);

  useEffect(() => {
    const el = mediaRef.current;
    const prevUrl = prevUrlRef.current;
    prevUrlRef.current = url;

    // Not a refresh: initial load, empty→real first-render race, unchanged
    // url, or teardown. Leave the element alone.
    if (!el || !prevUrl || !url || prevUrl === url) {
      return;
    }

    const savedTime = el.currentTime;
    const wasPaused = el.paused;

    const restore = () => {
      try {
        el.currentTime = savedTime;
      } catch {
        // Element may not be seekable yet on some browsers; best-effort only.
      }
      if (!wasPaused) {
        // play() returns a Promise in real browsers; guard for environments
        // (jsdom, older Safari) where it doesn't, and swallow autoplay
        // rejections rather than throwing out of an event handler.
        el.play()?.catch?.(() => {});
      }
    };

    el.addEventListener('loadedmetadata', restore, { once: true });
    return () => {
      el.removeEventListener('loadedmetadata', restore);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
}
